import { Injectable, OnDestroy } from '@angular/core';
import { Observable, BehaviorSubject, of, Subscription } from 'rxjs';
import { Router } from '@angular/router';
import { UserRoleEnum } from 'src/app/models/common-model';
import { SsoService } from './sso.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService implements OnDestroy {
  private readonly unsubscribe: Subscription[] = [];

  currentUserToken$: Observable<any>;
  isLoading$: Observable<boolean>;
  currentUserSubject: BehaviorSubject<any>;
  isLoadingSubject: BehaviorSubject<boolean>;
  isLoggedInSubject: BehaviorSubject<any>;
  userRole$: BehaviorSubject<any>;

  get currentUserValue(): any {
    return this.currentUserSubject.value;
  }

  set currentUserValue(user: any) {
    this.currentUserSubject.next(user);
  }

  private interceptorEnabled = false;

  enableInterceptor() {
    this.interceptorEnabled = true;
  }

  isInterceptorEnabled(): boolean {
    return this.interceptorEnabled;
  }

  private readonly subject = new BehaviorSubject<string>('');

  public currentUserRole = new BehaviorSubject<string>(this.getStoredRole());

  setValue(value: string) {
    this.subject.next(value);
  }

  getValue() {
    return this.subject.asObservable();
  }

  constructor(
    private readonly sso: SsoService,
    private readonly router: Router,
  ) {
    this.isLoadingSubject = new BehaviorSubject<boolean>(false);
    this.currentUserSubject = new BehaviorSubject<any>(undefined);
    this.currentUserToken$ = this.currentUserSubject.asObservable();
    this.isLoading$ = this.isLoadingSubject.asObservable();
  }

  /**
   * Retrieves the user data from the token. Called by the APP_INITIALIZER.
   * Bootstraps the SSO provider, populates the current user and role state.
   */
  getUserByToken(): Observable<any> {
    return new Observable((observer) => {
      this.sso
        .init()
        .then((authenticated) => {
          if (authenticated) {
            const token = this.sso.token;
            this.currentUserSubject.next(token);
            this.currentUserRole.next(this.getStoredRole());
            this.enableInterceptor();
            observer.next(token);
          } else {
            observer.next(undefined);
          }
          observer.complete();
        })
        .catch((err) => {
          console.error('SSO init failed', err);
          observer.next(undefined);
          observer.complete();
        });
    });
  }

  /**
   * Logs out via the SSO provider and redirects to the portal.
   */
  logout() {
    this.sso.logout();
  }

  setRole(role: string): void {
    localStorage.setItem('userRole', role);
    this.currentUserRole.next(role);
  }

  getRole(): Observable<string> {
    return this.currentUserRole.asObservable();
  }

  public getStoredRole(): string {
    return localStorage.getItem('userRole') ?? '';
  }

  checkUserAccess(): boolean {
    return (
      this.currentUserRole.value == UserRoleEnum[UserRoleEnum.Administrator] ||
      this.currentUserRole.value == UserRoleEnum[UserRoleEnum.AdvancedUser]
    );
  }

  ngOnDestroy() {
    this.unsubscribe.forEach((sb) => sb.unsubscribe());
  }
}
